import React, {useMemo, useState} from 'react';
import {Box, Chip, Grid, styled, TextField, Typography,} from '@mui/material';
import moment from 'moment';
import {useGetAllQuery} from '../../../services/rtkQueryServices';
import {DateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import config from "../../../assets/config";
import {FileUpload} from "../../../components/FileUpload";
import {v4 as uuid} from "uuid";
import {useAppSelector} from "../../../store/reduxHooks";
import {MuiColorInput} from "mui-color-input";
import EmailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";

const FormTopStyled = styled(Box)(({}) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
}));

const FormImageLeftStyled = styled(Box)(({}) => ({
    width: 200,
    height: 100,
    overflow: 'hidden',
    marginRight: 20,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
}));

const FromTestRightStyled = styled(Box)(({}) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
}));

const FormContactRowStyled = styled(Box)(({}) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
}));

const DateTimeStyled = styled(Box)(({theme}) => ({
    backgroundColor: theme.palette.error.main,
    width: '200px',
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.error.contrastText,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #CCC',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));


const DateTimeHoursContainerStyled = styled(Box)(({theme}) => ({
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.text.primary,
    width: '100%',
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: 5,
}));


const HeaderContainerStyled = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
}));

function OrganizerReadOnly({data}: any) {
    return (
        <>
            <Chip label={data.uuid} variant='outlined'/>
            <FormTopStyled>
                <FormImageLeftStyled
                    sx={{
                        backgroundImage: `url(${config.backendPath}/api/file/profile/${data.uuid})`,
                    }}
                ></FormImageLeftStyled>
                <FromTestRightStyled>
                    <Typography fontWeight={'bold'} fontSize={'1.5rem'}>
                        {data.name}
                    </Typography>
                    <Typography>{data.title}</Typography>
                </FromTestRightStyled>
            </FormTopStyled>
            <FormContactRowStyled>
                <EmailIcon/>
                <Typography key={`${data.id}-email`}>{data.email}</Typography>
            </FormContactRowStyled>
            <FormContactRowStyled>
                <PhoneIcon/>
                <Typography key={`${data.id}-mobile`}>{data.mobile}</Typography>
            </FormContactRowStyled>
            <Typography variant='h5' color={'GrayText'}>
                About
            </Typography>
            <Typography>{data.about}</Typography>
        </>
    );
}

function OrganizerEdit({data, setPayload}: any) {
    const [imageKey, setImageKey] = useState<string>(
        'profile_picture' + data._id + uuid()
    );
    const {token} = useAppSelector((state) => state.auth);

    const onFieldEdit = (e: any) => {
        setPayload({
            ...data,
            [e.target.name]: e.target.value,
        });
    };
    const { start,end } = data;

    const { startDate, endDate } = useMemo(() => {
        return {
            startDate: moment(start).format('DD MMM YYYY'),
            endDate: moment(end).format('DD MMM YYYY'),
        };
    }, [start, end]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Chip label={data.uuid} variant='outlined'/>
            </Grid>
            {data.uuid && (
                <Grid item xs={12}>
                    <FormImageLeftStyled
                        id={imageKey}
                        key={imageKey}
                        sx={{
                            backgroundImage: `url(${config.backendPath}/api/admin/v1/file/profile/${data.uuid}?q=${imageKey})`,
                        }}
                    />
                    <FileUpload
                        uploadUrl={`/api/admin/v1/file/upload/${data.uuid}`}
                        token={token}
                        onSuccess={() => {
                            setImageKey('profile_picture' + data._id + uuid());
                        }}
                    />
                </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
                <DateTimePicker
                    label='Event Start Date'
                    defaultValue={dayjs(start)}
                    onChange={(newValue) => setPayload({...data, start: newValue})}
                    sx={{width: '100%'}}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <DateTimePicker
                    label='Event Start Date'
                    defaultValue={dayjs(end)}
                    onChange={(newValue) => setPayload({...data, end: newValue})}
                    sx={{width: '100%'}}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label='Title'
                    name='title'
                    defaultValue={data.title}
                    size={'small'}
                    onBlur={onFieldEdit}
                    fullWidth={true}
                />
            </Grid>
            <Grid item xs={12}>
                <MuiColorInput name={'color'} label={'Color'} value={data.color} onChange={(newValue) => setPayload({...data, color: newValue})} format={'hex'} />
            </Grid>
        </Grid>
    );
}

export {OrganizerReadOnly, OrganizerEdit};
